import { formatCount, formatPercent, rateToInput } from '@/lib/format';
import type { DisplayNodes, FunnelResult } from '@/model/types';
import { useFunnelStore } from '@/store/useFunnelStore';
import { NumberField } from './NumberField';
import { DerivedNote, Note, Section } from './parts';

interface Props {
  result: FunnelResult;
  display: DisplayNodes;
}

export function SourceStage({ result, display }: Props) {
  const add = useFunnelStore((s) => s.addSubpopulation);
  const update = useFunnelStore((s) => s.updateSubpopulation);
  const remove = useFunnelStore((s) => s.removeSubpopulation);
  const addExclusion = useFunnelStore((s) => s.addExclusionPopulation);
  const updateExclusion = useFunnelStore((s) => s.updateExclusionPopulation);
  const removeExclusion = useFunnelStore((s) => s.removeExclusionPopulation);

  return (
    <>
      <Section title="Included source populations">
        <div className="crud-list">
          {result.subpopulations.map((sub, i) => {
            const shown = display.subpopulations[i];
            return (
              <div key={sub.id} className="crud-card">
                <div className="crud-head">
                  <input
                    className="crud-name"
                    type="text"
                    value={sub.name}
                    aria-label="Subpopulation name"
                    onChange={(event) => update(sub.id, { name: event.target.value })}
                  />
                  <button
                    type="button"
                    className="crud-delete"
                    aria-label={`Remove ${sub.name}`}
                    onClick={() => remove(sub.id)}
                  >
                    ×
                  </button>
                </div>

                <div className="crud-fields is-three">
                  <NumberField
                    label="Count"
                    value={Math.round(sub.count)}
                    step={1000}
                    onCommit={(count) => update(sub.id, { count })}
                  />
                  <NumberField
                    label="Reached"
                    value={rateToInput(sub.reach)}
                    max={100}
                    step={0.5}
                    suffix="%"
                    onCommit={(pct) => update(sub.id, { reach: pct / 100 })}
                  />
                  <NumberField
                    label="Screen rate"
                    value={rateToInput(sub.screenRate)}
                    max={100}
                    step={0.5}
                    suffix="%"
                    onCommit={(pct) => update(sub.id, { screenRate: pct / 100 })}
                  />
                </div>

                <p className="crud-derived is-centered">
                  {display.excludedPopulation > 0 && (
                    <>
                      <strong>{formatCount(shown?.count ?? 0)}</strong> eligible ·{' '}
                    </>
                  )}
                  {formatPercent(sub.share)} of outreach population ·{' '}
                  <strong>{formatCount(shown?.reached ?? 0)}</strong> reached →{' '}
                  <strong>{formatCount(shown?.screened ?? 0)}</strong> screened
                </p>
              </div>
            );
          })}
        </div>

        <button type="button" className="crud-add" onClick={add}>
          + Add subpopulation
        </button>
      </Section>

      <Section title="Excluded populations">
        <p className="drawer-section-intro">
          These groups are removed before outreach and from every population-based denominator.
        </p>

        {result.params.exclusionPopulations.length === 0 ? (
          <p className="crud-empty">No excluded populations.</p>
        ) : (
          <div className="crud-list">
            {result.params.exclusionPopulations.map((exclusion) => (
              <div key={exclusion.id} className="crud-card is-exclusion">
                <div className="crud-head">
                  <input
                    className="crud-name"
                    type="text"
                    value={exclusion.name}
                    aria-label="Excluded population name"
                    onChange={(event) =>
                      updateExclusion(exclusion.id, { name: event.target.value })
                    }
                  />
                  <button
                    type="button"
                    className="crud-delete"
                    aria-label={`Remove ${exclusion.name}`}
                    onClick={() => removeExclusion(exclusion.id)}
                  >
                    ×
                  </button>
                </div>

                <div className="crud-fields is-exclusion">
                  <NumberField
                    label="Excluded count"
                    value={Math.round(exclusion.count)}
                    step={1000}
                    onCommit={(count) => updateExclusion(exclusion.id, { count })}
                  />
                </div>

                <p className="crud-derived is-centered">
                  {result.population.source > 0
                    ? formatPercent(exclusion.count / result.population.source)
                    : formatPercent(0)}{' '}
                  of source population · not included in outreach
                </p>
              </div>
            ))}
          </div>
        )}

        <button type="button" className="crud-add is-exclusion" onClick={addExclusion}>
          + Add excluded population
        </button>

        <Note tone={result.population.unappliedExclusions > 0 ? 'warn' : 'info'}>
          Exclusions are allocated proportionally across source populations. Keep groups mutually
          exclusive to avoid double counting.
          {result.population.unappliedExclusions > 0 && (
            <>
              {' '}
              Exclusions exceed the source total by{' '}
              <strong>{formatCount(result.population.unappliedExclusions)}</strong>.
            </>
          )}
        </Note>
      </Section>

      <DerivedNote
        rows={[
          ['Source population', formatCount(display.sourcePopulation)],
          [
            'Excluded',
            display.excludedPopulation > 0
              ? `−${formatCount(display.excludedPopulation)}`
              : formatCount(0),
          ],
          ['Outreach population', formatCount(display.population)],
          ['Blended reach', formatPercent(result.rates.screeningReach)],
          ['Blended screen rate', formatPercent(result.rates.screenGivenReached)],
        ]}
      />
    </>
  );
}
